import { Component, signal, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    MessageModule
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.css'
})
export class ForgotPasswordComponent implements OnDestroy {
  email = signal('');
  otp = signal('');
  newPassword = signal('');
  confirmPassword = signal('');
  step = signal<'email' | 'otp' | 'reset'>('email');
  loading = signal(false);
  sendingOtp = signal(false);
  error = signal<string | null>(null);
  success = signal<string | null>(null);
  resetToken = signal<string | null>(null);
  expiresAt = signal<Date | null>(null);
  countdown = signal<number>(0);
  private countdownInterval: any;

  constructor(
    private router: Router,
    private authService: AuthService
  ) {}

  onSendOtp(): void {
    const emailValue = this.email().trim();
    
    if (!emailValue) {
      this.error.set('Please enter your email address');
      return;
    }

    if (!this.isValidEmail(emailValue)) {
      this.error.set('Please enter a valid email address');
      return;
    }

    this.sendingOtp.set(true);
    this.loading.set(false);
    this.error.set(null);
    this.success.set(null);
    this.otp.set('');

    this.authService.sendForgotPasswordOtp(emailValue).subscribe({
      next: (response) => {
        this.sendingOtp.set(false);
        if (response.success) {
          this.step.set('otp');
          this.success.set('Password reset code sent to your email!');
          if (response.data?.expiresAt) {
            this.expiresAt.set(new Date(response.data.expiresAt));
            this.startCountdown();
          }
        } else {
          this.error.set(response.message || 'Failed to send password reset code');
        }
      },
      error: (err) => {
        this.sendingOtp.set(false);
        this.error.set(err.error?.message || 'An error occurred. Please try again.');
      }
    });
  }

  onVerifyOtp(): void {
    const otpValue = this.otp().trim();

    if (!otpValue || otpValue.length !== 6) {
      this.error.set('Please enter a valid 6-digit OTP');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.authService.verifyForgotPasswordOtp(this.email().trim(), otpValue).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success && response.data?.resetToken) {
          this.resetToken.set(response.data.resetToken);
          this.step.set('reset');
        } else {
          this.error.set(response.message || 'Invalid or expired verification code');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err.error?.message || 'Invalid verification code. Please try again.');
      }
    });
  }

  onResetPassword(): void {
    if (!this.newPassword().trim()) {
      this.error.set('Please enter a new password');
      return;
    }

    if (this.newPassword().length < 6) {
      this.error.set('Password must be at least 6 characters long');
      return;
    }

    if (this.newPassword() !== this.confirmPassword()) {
      this.error.set('Passwords do not match');
      return;
    }

    const resetTokenValue = this.resetToken();
    if (!resetTokenValue) {
      this.error.set('Invalid reset token. Please start the password reset process again.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.authService.resetPassword(resetTokenValue, this.newPassword().trim()).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.router.navigate(['/signin']);
        } else {
          this.error.set(response.message || 'Failed to reset password');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err.error?.message || 'Failed to reset password. Please try again.');
      }
    });
  }

  onResendOtp(): void {
    const emailValue = this.email().trim();
    
    if (!emailValue) {
      this.error.set('Email address is required');
      return;
    }

    this.sendingOtp.set(true);
    this.otp.set('');
    this.error.set(null);
    this.success.set(null);

    this.authService.resendForgotPasswordOtp(emailValue).subscribe({
      next: (response) => {
        this.sendingOtp.set(false);
        if (response.success) {
          this.success.set('Password reset code resent to your email!');
          if (response.data?.expiresAt) {
            this.expiresAt.set(new Date(response.data.expiresAt));
            this.startCountdown();
          }
        } else {
          this.error.set(response.message || 'Failed to resend password reset code');
        }
      },
      error: (err) => {
        this.sendingOtp.set(false);
        this.error.set(err.error?.message || 'An error occurred. Please try again.');
      }
    });
  }

  formatCountdown(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private startCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    const updateCountdown = () => {
      if (!this.expiresAt()) {
        return;
      }

      const now = new Date();
      const expires = this.expiresAt()!;
      const diff = Math.max(0, Math.floor((expires.getTime() - now.getTime()) / 1000));
      
      this.countdown.set(diff);

      if (diff <= 0) {
        clearInterval(this.countdownInterval);
        this.countdown.set(0);
      }
    };

    updateCountdown();
    this.countdownInterval = setInterval(updateCountdown, 1000);
  }

  goBackToEmail(): void {
    this.step.set('email');
    this.otp.set('');
    this.error.set(null);
  }

  goBackToOtp(): void {
    this.step.set('otp');
    this.newPassword.set('');
    this.confirmPassword.set('');
    this.error.set(null);
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }
}
